// THE sales funnel. One list, imported everywhere it is shown.
//
// ⛔ WHY THIS FILE EXISTS. The campaign page and the Leads page each carried their own
// hard-coded copy. Adding the three "email sent" stages to Leads (v1.86.0) left the
// campaign page a version behind, so the same contact offered different options
// depending on which screen you opened it from — which is how a funnel stops meaning
// anything.
//
// It is the same shape as the Cartesia bug still open with Charlie: a vendor list
// re-typed into a second file. A list maintained in two places is maintained in
// neither.
//
// ⚠️ Values are validated server-side against SALES_STAGES in
// core/src/services/campaignService.js. This file may not invent one — a value core
// does not know 400s. Order matters there too: core derives SALES_STAGE_RANK from
// array position, which drives its priority sort.

/** Funnel order. `value` is stored on the contact; `label` is what a person reads. */
export const SALES_STAGE_OPTIONS = [
  { value: '', label: '— None —' },
  { value: 'answered_call', label: 'Answered call' },
  { value: 'decision_maker_interested', label: 'Decision-maker interested' },
  { value: 'gatekeeper_interested', label: 'Gatekeeper interested' },
  // Reached + interested but no close — the recover-later bucket.
  { value: 'interested_no_close', label: 'Interested — no close' },
  { value: 'send_email', label: 'Send email' },
  // ── SENT ── what WE did, as opposed to what the call produced. Without these the
  // list cannot separate "still owed" from "waiting on a reply".
  { value: 'emailed_brochure', label: 'Email sent — brochure' },
  { value: 'emailed_demo', label: 'Email sent — demo number' },
  { value: 'emailed', label: 'Email sent' },
  { value: 'schedule_demo', label: 'Schedule demo' },
  { value: 'schedule_trial', label: 'Schedule trial' }
];

/** Stages core does not carry YET, so a selection 400s until Charlie ships the enum.
 *  Callers explain that rather than surfacing a raw rejection. */
export const STAGES_PENDING_IN_CORE = new Set(['emailed_brochure', 'emailed_demo', 'emailed']);

/** A real person is leaning in — pinned above everything else wherever contacts sort. */
export const HOT_LEAD_STAGES = new Set(['gatekeeper_interested', 'decision_maker_interested']);

/** Stages that mean somebody is waiting on US. Drives the Leads page. Ordered by heat. */
export const FOLLOW_UP_STAGES = [
  { key: 'decision_maker_interested', hot: true, note: 'The person who can say yes is leaning in. Highest priority.' },
  { key: 'gatekeeper_interested', hot: true, note: 'Front desk is willing to help — the way in.' },
  { key: 'send_email', hot: false, note: 'Asked for something in writing. Still owed — send it manually.' },
  { key: 'emailed_brochure', hot: false, note: 'Brochure went out. Waiting on a reply.' },
  { key: 'emailed_demo', hot: false, note: 'Demo line went out. Waiting on a reply.' },
  { key: 'emailed', hot: false, note: 'Sent. Waiting on a reply.' },
  { key: 'schedule_demo', hot: false, note: 'Wants to see it.' },
  { key: 'schedule_trial', hot: false, note: 'Ready to try it.' },
  { key: 'interested_no_close', hot: false, note: 'Reached and interested, never closed. The recover-later bucket.' }
];

export const stageLabel = (value) =>
  SALES_STAGE_OPTIONS.find((o) => o.value === (value || ''))?.label || value || '— None —';
